// FIXED VERSION: /src/pages/admin/Brands.jsx
// Admin page for managing brands with PROPER error handling

import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import BrandList from '../../components/admin/BrandList';
import BrandForm from '../../components/admin/BrandForm';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import './Brands.css';

const Brands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, brand: null });
  const [error, setError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false); // ✅ ADDED

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/brands', {
        params: { all_languages: true }
      });
      setBrands(response.data.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
      setError('Failed to load brands');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingBrand(null);
    setShowForm(true);
    setError('');
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setShowForm(true);
    setError('');
  };

  const handleDelete = (brand) => {
    setDeleteModal({ isOpen: true, brand });
    setError(''); // ✅ Clear previous errors
  };

  // ✅ FIXED: Better error handling and logging
  const confirmDelete = async () => {
    const brand = deleteModal.brand;
    
    console.log('🗑️ Attempting to delete brand:', brand.id, brand.name_ka || brand.name);
    
    try {
      setDeleteLoading(true);
      setError('');
      
      // Make the API call
      const response = await api.delete(`/admin/brands/${brand.id}`);
      
      console.log('✅ Delete response:', response.data);
      
      // ✅ Only close modal and update list on SUCCESS
      setBrands(brands.filter(b => b.id !== brand.id));
      setDeleteModal({ isOpen: false, brand: null });
      
      // Show success message (optional)
      // You could add a success state/toast here
      
    } catch (error) {
      console.error('❌ Delete error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // ✅ Show error but KEEP modal OPEN
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message ||
                          'Failed to delete brand';
      setError(errorMessage);
      
      // ✅ DON'T close modal on error - let user see the error and try again
      // setDeleteModal({ isOpen: false, brand: null }); // ❌ REMOVED
      
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      setFormLoading(true);
      setError('');

      if (editingBrand) {
        // Update
        await api.put(`/admin/brands/${editingBrand.id}`, formData);
      } else {
        // Create
        await api.post('/admin/brands', formData);
      }

      await fetchBrands();
      setShowForm(false);
      setEditingBrand(null);
    } catch (error) {
      console.error('Submit error:', error);
      setError(error.response?.data?.error || 'Failed to save brand');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingBrand(null);
    setError('');
  };

  // ✅ ADDED: Close delete modal manually
  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, brand: null });
    setError('');
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="spinner"></div>
            <p>Loading brands...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="page-header">
          <div>
            <h1>🏷️ ბრენდები (Brands)</h1>
            <p className="page-description">
              Manage product brands with multi-language support
            </p>
          </div>
          {!showForm && (
            <Button variant="primary" onClick={handleAdd}>
              + Add Brand
            </Button>
          )}
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            {error}
            <button 
              onClick={() => setError('')}
              style={{ 
                marginLeft: '1rem', 
                background: 'none', 
                border: 'none', 
                color: 'inherit',
                cursor: 'pointer',
                fontSize: '1.2rem'
              }}
            >
              ×
            </button>
          </div>
        )}

        {showForm ? (
          <div className="form-section">
            <div className="form-header">
              <h2>{editingBrand ? 'Edit Brand' : 'Add New Brand'}</h2>
            </div>
            <BrandForm
              brand={editingBrand}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              loading={formLoading}
            />
          </div>
        ) : (
          <BrandList
            brands={brands}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        )}

        {/* ✅ IMPROVED: Shows error inside modal */}
        <ConfirmModal
          isOpen={deleteModal.isOpen}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
          title="Delete Brand"
          message={
            <>
              {error && (
                <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                  {error}
                </div>
              )}
              Are you sure you want to delete "{deleteModal.brand?.name || deleteModal.brand?.name_ka}"?
              {deleteModal.brand?.product_count > 0 && (
                <div style={{ marginTop: '0.5rem', color: '#dc3545', fontWeight: 'bold' }}>
                  ⚠️ This brand has {deleteModal.brand.product_count} products!
                </div>
              )}
            </>
          }
          type="danger"
          loading={deleteLoading}
        />
      </div>
    </div>
  );
};

export default Brands;